import React from "react";
import {
  FiMessageSquare,
  FiShoppingCart,
  FiGlobe,
  FiRepeat,
  FiLayout,
  FiLink,
} from "react-icons/fi";
import { MdOutlineWhatsapp, MdOutlineLanguage } from "react-icons/md";
import AIAutomationTemplate from "./AIAutomationTemplate";

const AIChatbot = () => {
  const features = [
    {
      title: "Enterprise NLP Chatbots",
      description:
        "Deploy GPT-class conversational bots tailored with your knowledge base, tone, and compliance requirements.",
      icon: <FiMessageSquare />,
    },
    {
      title: "Website & WhatsApp Integration",
      description:
        "Unified bot experiences embedded across web widgets, WhatsApp, Messenger, and in-app experiences.",
      icon: <MdOutlineWhatsapp />,
    },
    {
      title: "E-commerce Automation",
      description:
        "Bots handle catalog discovery, cart recovery, upsell recommendations, and payment coordination.",
      icon: <FiShoppingCart />,
    },
    {
      title: "Smart Conversation Flows",
      description:
        "Adaptive flows branch intelligently based on intent, sentiment, and purchase probability.",
      icon: <FiRepeat />,
    },
    {
      title: "Multilingual Localizations",
      description:
        "Serve global audiences with localized content, currency awareness, and region-specific compliance.",
      icon: <MdOutlineLanguage />,
    },
    {
      title: "Backend & API Integrations",
      description:
        "Secure API connectors pull customer, order, booking, and policy data in real time to resolve requests.",
      icon: <FiLink />,
    },
  ];

  const steps = [
    {
      title: "Intent Discovery",
      description:
        "Analyze chat logs, FAQs, and customer journeys to prioritize target intents and success metrics.",
    },
    {
      title: "Conversation Design",
      description:
        "Craft branded flows, guardrails, and escalation paths with experience designers and CX strategists.",
    },
    {
      title: "Bot Build & Integration",
      description:
        "Configure NLP models, channel adapters, and backend integrations, then test for accuracy and tone.",
    },
    {
      title: "Optimization & Growth",
      description:
        "Monitor CSAT, AHT, and conversion to continuously expand coverage and maximize ROI.",
    },
  ];

  const benefits = [
    "Deliver consistent answers 24/7 across every channel",
    "Increase conversions with guided selling and proactive nudges",
    "Lower support costs by automating repetitive queries",
    "Improve personalization using first-party context and preferences",
    "Accelerate rollout with reusable components and playbooks",
    "Gain insights into customer sentiment and emerging needs",
  ];

  const expertise = [
    "Certified WhatsApp Business Solution Provider partnerships",
    "Experience across Dialogflow, Rasa, Botpress, Azure Bot Framework, and custom GPT stacks",
    "Design system alignment to maintain brand voice globally",
    "Robust analytics for drop-off analysis and optimization",
    "Data privacy and consent frameworks meeting GDPR/CCPA",
    "Dedicated QA lab for regression testing across devices and channels",
  ];

  const image = [
    {
      side_image: "../assets/images/ai-automation/5.png",
    }

  ];

  const useCases = [
    {
      industry: "Retail",
      title: "Lifestyle Commerce Advisor",
      description:
        "Chatbot guides shoppers through curated looks, stock availability, and checkout across web + WhatsApp.",
    },
    {
      industry: "Travel",
      title: "Trip Concierge",
      description:
        "Bots book flights, manage itinerary changes, and upsell ancillaries with real-time inventory access.",
    },
    {
      industry: "Banking",
      title: "Secure Banking Assistant",
      description:
        "Customers check balances, reset cards, and dispute transactions with MFA-backed flows.",
    },
    {
      industry: "Education",
      title: "Admissions Counselor",
      description:
        "Conversational AI answers program questions, schedules interviews, and tracks document submissions.",
    },
  ];

  const reasons = [
    "UX writers and CX strategists ensure on-brand conversational tone",
    "Reusable templates for sales, support, onboarding, and retention",
    "Observability stack with journey analytics and sentiment heatmaps",
    "ModelOps governance covering prompts, testing, and drift control",
    "Security-first integrations with encrypted data exchange",
    "Global delivery with multilingual SMEs and 24/7 support",
  ];

  return (
    <AIAutomationTemplate
      title="AI Chatbot Development"
      heroDescription="Launch intelligent chatbots that combine natural language understanding, omnichannel reach, and business system integrations."
      about={{
        heading: "About AI Chatbot Development",
        description:
          "We craft conversational AI that feels human, scales globally, and plugs into your commerce, service, and marketing stacks.",
      }}
      features={features}
      steps={steps}
      benefits={benefits}
      expertise={expertise}
      image={image}
      useCases={useCases}
      reasons={reasons}
      ctaLabel="Design a Conversational Bot"
      meta={{
        title: "AI Chatbot Development | Conversational AI Studio | Ayanshtech",
        description:
          "NLP chatbots with website & WhatsApp integration, e-commerce automation, and smart conversation flows.",
        ogTitle: "AI Chatbot Development Services",
        ogDescription:
          "Build intelligent chatbots across web and messaging channels with deep business integrations.",
        ogUrl: "http://ayanshtechnology.com/ai-chatbot-development",
        canonical: "http://ayanshtechnology.com/ai-chatbot-development",
      }}
    />
  );
};

export default AIChatbot;


